import { useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'
import type { Control, FieldErrors } from 'react-hook-form'

import {
  PAYMENT_BALANCE_MODE,
  balanceMethodOptions,
  balanceTermBeginsOptions,
  balanceTimeOptions,
  paymentMethodOptions,
  paymentTermsOptions,
  PaymentSettlementBanner,
} from '#/entities/shipment'
import type { PaymentFormulaResult } from '#/entities/shipment'
import { MarketRateDialog, useSuggestedPrice } from '#/features/market-rate'
import type { MarketRateRow } from '#/features/market-rate'
import { formatUsd } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import { FormCard } from '#/shared/ui/form-card'
import { Input } from '#/shared/ui/input'
import { Label } from '#/shared/ui/label'
import { PriceCheckBadge } from '#/shared/ui/price-check-badge'
import { Select } from '#/shared/ui/select'
import { usePaymentEditForm } from '../model/use-payment-edit-form'
import type { PaymentFormData } from '../model/schema'

interface PaymentEditFormProps {
  shipmentId?: string
  defaultValues?: Partial<PaymentFormData>
  onSubmit: (data: PaymentFormData) => void | Promise<void>
  onCancel?: () => void
  showCarrierDetails?: boolean
  priceMode?: boolean
  className?: string
  contentClassName?: string
  actionsAtBottom?: boolean
  /** 'section' renders the compact uppercase header used in the sidebar. */
  headerVariant?: 'default' | 'section'
  /** Skips the `edit-form-fields` gray fill so inputs stay white (e.g. on a
   *  gray form card in the sidebar). */
  plainInputs?: boolean
  /** Rendered inline after the title (e.g. the market-rate metrics). */
  titleExtra?: ReactNode
  /** Moves the rate trigger to the header's right as a "Sample Price" link
   *  instead of an inline "Check Rate" badge (matches the sidebar view). */
  priceCheckOnRight?: boolean
}

function FormFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col items-end gap-2.5 pt-1.25">
      <div className="h-px w-full bg-gray-200" />
      <div className="flex items-center gap-2.5">{children}</div>
    </div>
  )
}

const FIELD_PROPS = {
  inputSize: 'sm' as const,
  labelSize: 'sm' as const,
  clearable: true,
}

const MONEY_PROPS = {
  ...FIELD_PROPS,
  mask: '$',
  numberFormat: true,
  placeholder: '0.00',
}

function MoneyField({
  name,
  label,
  control,
  errors,
  width,
}: {
  name: 'totalTariff' | 'deposit'
  label: string
  control: Control<PaymentFormData>
  errors: FieldErrors<PaymentFormData>
  width: string
}) {
  return (
    <div className={cn('min-w-0', width)}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Input
            {...MONEY_PROPS}
            label={label}
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            name={field.name}
            error={!!errors[name]}
          />
        )}
      />
    </div>
  )
}

function CarrierFeeField({
  label,
  control,
  errors,
  width,
  onUserType,
  onUserClear,
}: {
  label: string
  control: Control<PaymentFormData>
  errors: FieldErrors<PaymentFormData>
  width: string
  onUserType: () => void
  onUserClear: () => void
}) {
  return (
    <div className={cn('min-w-0', width)}>
      <Controller
        control={control}
        name="carrierFee"
        render={({ field }) => (
          <Input
            {...MONEY_PROPS}
            label={label}
            value={field.value}
            onChange={(e) => {
              const v = e.target.value
              if (v === '') onUserClear()
              else onUserType()
              field.onChange(v)
            }}
            onBlur={field.onBlur}
            name={field.name}
            error={!!errors.carrierFee}
          />
        )}
      />
    </div>
  )
}

function ReadOnlyMoneyField({
  label,
  amount,
  width,
}: {
  label: string
  amount: number
  width: string
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-0.5', width)}>
      <Label size="sm">{label}</Label>
      <div className="flex h-8 items-center overflow-hidden rounded-sm border border-gray-200 bg-gray-100 px-2 text-sm whitespace-nowrap text-gray-900">
        {formatUsd(amount, { showCents: true })}
      </div>
    </div>
  )
}

type SelectFieldName =
  | 'paymentMethod'
  | 'paymentTerms'
  | 'balanceMethod'
  | 'balanceTime'
  | 'balanceTermBegins'

function SelectField({
  name,
  label,
  control,
  options,
  width,
  required,
}: {
  name: SelectFieldName
  label: string
  control: Control<PaymentFormData>
  options: ReturnType<typeof paymentMethodOptions>
  width: string
  required?: boolean
}) {
  const labelText = required ? `${label} *` : label
  return (
    <div className={cn('min-w-0', width)}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            label={labelText}
            labelSize="sm"
            selectSize="sm"
            options={options}
            value={field.value || undefined}
            onValueChange={field.onChange}
          />
        )}
      />
    </div>
  )
}

function FormTitle({
  onPriceCheck,
  priceMode,
  variant = 'default',
  titleExtra,
  hideBadge = false,
}: {
  onPriceCheck: () => void
  priceMode?: boolean
  variant?: 'default' | 'section'
  titleExtra?: ReactNode
  hideBadge?: boolean
}) {
  const { t } = useTranslation()
  return (
    <span className="flex items-center gap-2.5">
      <span
        className={
          variant === 'default'
            ? 'text-base font-semibold text-gray-900'
            : undefined
        }
      >
        {priceMode ? t('payment.priceTitle') : t('payment.title')}
      </span>
      {/* normal-case keeps the metrics/badge readable under the uppercase header */}
      {titleExtra && <span className="normal-case">{titleExtra}</span>}
      {!hideBadge && (
        <span className="normal-case">
          <PriceCheckBadge
            label={t('payment.priceCheck')}
            onClick={onPriceCheck}
          />
        </span>
      )}
    </span>
  )
}

function FormActions({
  onCancel,
  isSubmitting,
  priceMode,
  submitDisabled,
}: {
  onCancel?: () => void
  isSubmitting: boolean
  priceMode?: boolean
  submitDisabled?: boolean
}) {
  const { t } = useTranslation()
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="xs"
        pressEffect
        className="px-3.5"
        onClick={onCancel}
      >
        {t('payment.cancel')}
      </Button>
      <Button
        type="submit"
        size="xs"
        pressEffect
        className="px-3.5"
        disabled={isSubmitting || submitDisabled}
      >
        {priceMode ? t('payment.convertToQuote') : t('payment.saveChanges')}
      </Button>
    </>
  )
}

function PaymentFieldsRow({
  control,
  errors,
  formula,
  showCarrierDetails,
  markCarrierManual,
  releaseCarrierManual,
}: {
  control: Control<PaymentFormData>
  errors: FieldErrors<PaymentFormData>
  formula: PaymentFormulaResult
  showCarrierDetails: boolean
  markCarrierManual: () => void
  releaseCarrierManual: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex items-start gap-2.5">
      <MoneyField
        name="totalTariff"
        label={`${t('payment.totalTariff')} *`}
        control={control}
        errors={errors}
        width="flex-1"
      />
      <MoneyField
        name="deposit"
        label={`${t('payment.deposit')} *`}
        control={control}
        errors={errors}
        width="flex-1"
      />
      {showCarrierDetails && (
        <CarrierFeeField
          label={`${t('payment.carrierFee')} *`}
          control={control}
          errors={errors}
          width="flex-1"
          onUserType={markCarrierManual}
          onUserClear={releaseCarrierManual}
        />
      )}
      {showCarrierDetails && (
        <ReadOnlyMoneyField
          label={t('payment.dueOnDelivery')}
          amount={formula.dueOnDelivery}
          width="flex-1"
        />
      )}
    </div>
  )
}

function CodSelectsRow({ control }: { control: Control<PaymentFormData> }) {
  const { t } = useTranslation()
  return (
    <div className="flex animate-slide-down items-start gap-2.5">
      <SelectField
        name="paymentMethod"
        label={t('payment.paymentMethod')}
        control={control}
        options={paymentMethodOptions(t)}
        width="flex-1"
        required
      />
      <SelectField
        name="paymentTerms"
        label={t('payment.terms')}
        control={control}
        options={paymentTermsOptions(t)}
        width="flex-1"
        required
      />
    </div>
  )
}

function BalanceRow({
  control,
  formula,
  showCodSelects,
}: {
  control: Control<PaymentFormData>
  formula: PaymentFormulaResult
  showCodSelects: boolean
}) {
  const { t } = useTranslation()
  if (showCodSelects) {
    return (
      <div className="flex animate-slide-down items-start gap-2.5">
        <ReadOnlyMoneyField
          label={t('payment.balance')}
          amount={formula.balanceAbsolute}
          width="flex-1"
        />
        <SelectField
          name="paymentMethod"
          label={t('payment.paymentMethod')}
          control={control}
          options={paymentMethodOptions(t)}
          width="flex-1"
          required
        />
        <SelectField
          name="paymentTerms"
          label={t('payment.terms')}
          control={control}
          options={paymentTermsOptions(t)}
          width="flex-1"
          required
        />
      </div>
    )
  }
  return (
    <div className="flex animate-slide-down items-start gap-2.5">
      <ReadOnlyMoneyField
        label={t('payment.balance')}
        amount={formula.balanceAbsolute}
        width="w-22.5 shrink-0"
      />
      <SelectField
        name="balanceMethod"
        label={t('payment.balanceMethod')}
        control={control}
        options={balanceMethodOptions(t)}
        width="flex-1"
        required
      />
      <SelectField
        name="balanceTime"
        label={t('payment.balanceTime')}
        control={control}
        options={balanceTimeOptions(t)}
        width="flex-1"
        required
      />
      <SelectField
        name="balanceTermBegins"
        label={t('payment.balanceTermBegins')}
        control={control}
        options={balanceTermBeginsOptions(t)}
        width="flex-1"
        required
      />
    </div>
  )
}

function SettlementSelectsRow({
  control,
}: {
  control: Control<PaymentFormData>
}) {
  const { t } = useTranslation()
  return (
    <div className="flex animate-slide-down items-start gap-2.5">
      <SelectField
        name="balanceMethod"
        label={t('payment.balanceMethod')}
        control={control}
        options={balanceMethodOptions(t)}
        width="flex-1"
        required
      />
      <SelectField
        name="balanceTime"
        label={t('payment.balanceTime')}
        control={control}
        options={balanceTimeOptions(t)}
        width="flex-1"
        required
      />
      <SelectField
        name="balanceTermBegins"
        label={t('payment.balanceTermBegins')}
        control={control}
        options={balanceTermBeginsOptions(t)}
        width="flex-1"
        required
      />
    </div>
  )
}

interface PaymentSectionFlags {
  showCodSelects: boolean
  showBalanceRow: boolean
  showSettlementSelects: boolean
  showBanner: boolean
}

function resolveSectionFlags(
  formula: PaymentFormulaResult,
  showCarrierDetails: boolean,
): PaymentSectionFlags {
  if (!showCarrierDetails) {
    return {
      showCodSelects: false,
      showBalanceRow: false,
      showSettlementSelects: false,
      showBanner: false,
    }
  }
  const isCarrierOwes = formula.mode === PAYMENT_BALANCE_MODE.CARRIER_OWES_US
  const isCodOnly = formula.mode === PAYMENT_BALANCE_MODE.COD_ONLY
  return {
    showCodSelects: isCodOnly || isCarrierOwes,
    showBalanceRow: formula.hasBalance,
    showSettlementSelects: isCarrierOwes,
    showBanner: formula.mode !== PAYMENT_BALANCE_MODE.NONE,
  }
}

function PaymentBody({
  control,
  errors,
  formula,
  selectValues,
  showCarrierDetails,
  markCarrierManual,
  releaseCarrierManual,
  register,
  t,
}: {
  control: Control<PaymentFormData>
  errors: FieldErrors<PaymentFormData>
  formula: PaymentFormulaResult
  selectValues: ReturnType<typeof usePaymentEditForm>['selectValues']
  showCarrierDetails: boolean
  markCarrierManual: () => void
  releaseCarrierManual: () => void
  register: ReturnType<typeof usePaymentEditForm>['register']
  t: ReturnType<typeof useTranslation>['t']
}) {
  const flags = resolveSectionFlags(formula, showCarrierDetails)
  return (
    <>
      <PaymentFieldsRow
        control={control}
        errors={errors}
        formula={formula}
        showCarrierDetails={showCarrierDetails}
        markCarrierManual={markCarrierManual}
        releaseCarrierManual={releaseCarrierManual}
      />
      {flags.showBalanceRow && (
        <BalanceRow
          control={control}
          formula={formula}
          showCodSelects={flags.showCodSelects}
        />
      )}
      {!flags.showBalanceRow && flags.showCodSelects && (
        <CodSelectsRow control={control} />
      )}
      {flags.showSettlementSelects && (
        <SettlementSelectsRow control={control} />
      )}
      {flags.showBanner && (
        <PaymentSettlementBanner formula={formula} values={selectValues} />
      )}
      {showCarrierDetails && (
        <Input
          {...FIELD_PROPS}
          {...register('paymentNote')}
          label={t('payment.paymentNote')}
          placeholder={t('payment.paymentNotePlaceholder')}
        />
      )}
    </>
  )
}

function contentClasses(plainInputs: boolean, contentClassName?: string) {
  // `edit-form-fields` paints inputs gray; skip it when they should stay white.
  return cn(!plainInputs && 'edit-form-fields', 'gap-3', contentClassName)
}

function samplePriceAction(
  show: boolean,
  onClick: () => void,
  label: string,
): ReactNode {
  if (!show) return undefined
  return (
    <span className="normal-case">
      <PriceCheckBadge label={label} onClick={onClick} />
    </span>
  )
}

export function PaymentEditForm({
  shipmentId,
  defaultValues,
  onSubmit,
  onCancel,
  showCarrierDetails = true,
  priceMode = false,
  className,
  contentClassName,
  actionsAtBottom = false,
  headerVariant = 'default',
  plainInputs = false,
  titleExtra,
  priceCheckOnRight,
}: PaymentEditFormProps) {
  const { t } = useTranslation()
  const [marketRateOpen, setMarketRateOpen] = useState(false)
  const form = usePaymentEditForm({ defaultValues, onSubmit })
  const priceFieldsIncomplete =
    form.tariff.trim().length === 0 || form.deposit.trim().length === 0
  const suggestion = useSuggestedPrice({
    shipmentId,
    enabled: marketRateOpen,
  })

  const openRate = () => setMarketRateOpen(true)
  const actions = (
    <FormActions
      onCancel={onCancel}
      isSubmitting={form.isSubmitting}
      priceMode={priceMode}
      submitDisabled={priceMode && priceFieldsIncomplete}
    />
  )
  const title = (
    <FormTitle
      onPriceCheck={openRate}
      priceMode={priceMode}
      variant={headerVariant}
      titleExtra={titleExtra}
      hideBadge={priceCheckOnRight}
    />
  )
  const rightAction = samplePriceAction(
    !!priceCheckOnRight,
    openRate,
    t('payment.samplePrice'),
  )

  return (
    <FormCard
      as="form"
      variant={headerVariant}
      onSubmit={form.handleSubmit}
      className={className}
      contentClassName={contentClasses(plainInputs, contentClassName)}
      title={title}
      actions={actionsAtBottom ? rightAction : actions}
    >
      <PaymentBody
        control={form.control}
        errors={form.errors}
        formula={form.formula}
        selectValues={form.selectValues}
        showCarrierDetails={showCarrierDetails}
        markCarrierManual={form.markCarrierManual}
        releaseCarrierManual={form.releaseCarrierManual}
        register={form.register}
        t={t}
      />
      {actionsAtBottom && <FormFooter>{actions}</FormFooter>}
      <RateDialog
        open={marketRateOpen}
        onOpenChange={setMarketRateOpen}
        suggestion={suggestion}
        onUseRate={(row) =>
          form.applyTariff(row.actualPrice ?? row.listedPrice)
        }
      />
    </FormCard>
  )
}

function RateDialog({
  open,
  onOpenChange,
  suggestion,
  onUseRate,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  suggestion: ReturnType<typeof useSuggestedPrice>
  onUseRate: (row: MarketRateRow) => void
}) {
  return (
    <MarketRateDialog
      open={open}
      onOpenChange={onOpenChange}
      stats={suggestion.stats}
      rows={suggestion.rows}
      isLoading={suggestion.isLoading}
      errorMessage={suggestion.errorMessage}
      onUseRate={onUseRate}
    />
  )
}
